import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { NEW, EXISTING } from '../../../../../utils/constants';

import Modal from '../../../../common/modal/Modal';
import TabBar from '../../../../common/tab-bar/TabBar';
import TextInput from '../../../../common/inputs/text-input/TextInput';
import Dropdown from '../../../../common/inputs/dropdown/Dropdown';
import Button from '../../../../common/button/Button';

import './product-category-modal.scss';

const ProductCategoryModal = ({ show, setShow, retrieveCategories, createCategory, updateCategory }) => {
  const { classifications, categories } = useSelector(({ product }) => product);

  const [tab, setTab] = useState(NEW);
  const [classification, setClassification] = useState(undefined);
  const [existingCategory, setExistingCategory] = useState(undefined);
  const [categoryName, setCategoryName] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!show) {
      setTab(NEW);
      setClassification(undefined);
      setExistingCategory(undefined);
      setCategoryName('');
      setErrors({});
    }
  }, [show]);

  const handleTabChange = (tab) => {
    setTab(tab);
    setClassification(undefined);
    setExistingCategory(undefined);
    setCategoryName('');
    setErrors({});
  };

  const handleClassificationChange = (option) => {
    if (option.externalReference !== classification?.externalReference) {
      setClassification(option);

      if (tab === EXISTING && !existingCategory) {
        retrieveCategories(option.externalReference);
        setExistingCategory(undefined);
        setCategoryName('');
      }
    }
  };

  const handleCategoryChange = (category) => {
    setExistingCategory(category);
    setCategoryName(category.name);
    setErrors({});
  };

  const handleCategoryNameChange = (event) => {
    setCategoryName(event.target.value);
    setErrors({});
  };

  const validateFields = () => {
    const errors = {};

    if (!classification) errors.classification = true;

    if (tab === EXISTING && !existingCategory) {
      errors.category = true;
    }

    if (!categoryName) errors.categoryName = true;

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveClick = () => {
    const valid = validateFields();
    if (!valid) return;

    const category = {
      classificationExternalReference: classification.externalReference,
      categoryExternalReference: undefined,
      categoryName,
    };

    if (tab === NEW) {
      createCategory(category, () => setShow(false));
    } else {
      category.categoryExternalReference = existingCategory.externalReference;
      updateCategory(category, () => setShow(false));
    }
  };

  return (
    <Modal show={show} setShow={setShow}>
      <section className="product-classification-modal">
        <h1>Manage Product Categories</h1>

        <TabBar tab={tab} tabs={[NEW, EXISTING]} onTabChange={handleTabChange} />

        <label htmlFor="classification">Product Classification</label>
        <Dropdown
          name="classification"
          value={classification}
          options={classifications}
          onChange={handleClassificationChange}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.externalReference}
          error={errors.classification}
        />

        {tab === EXISTING && (
          <>
            <label htmlFor="category">Product Category</label>
            <Dropdown
              name="category"
              value={existingCategory}
              options={categories}
              onChange={handleCategoryChange}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              disabled={!classification}
              error={errors.category}
            />
          </>
        )}

        <label htmlFor="category-name">Product Category Name</label>
        <TextInput
          name="category-name"
          value={categoryName}
          onChange={handleCategoryNameChange}
          disabled={!classification || (tab === EXISTING && !existingCategory)}
          error={errors.categoryName}
        />

        <div className="product-classification-modal__buttons">
          <Button onClick={() => setShow(false)} secondary>
            Cancel
          </Button>
          <Button onClick={handleSaveClick} primary>
            Save
          </Button>
        </div>
      </section>
    </Modal>
  );
};

ProductCategoryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
  retrieveCategories: PropTypes.func.isRequired,
  createCategory: PropTypes.func.isRequired,
  updateCategory: PropTypes.func.isRequired,
};

export default ProductCategoryModal;
